// MarketingDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';

export const MarketingDialog = ({ open, onOpenChange, accountId, onSuccess, contactEntries = [] }) => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [formData, setFormData] = useState({
    campaign_id: '',
    contact_entry_id: '',
  });

  useEffect(() => {
    if (open) {
      fetchCampaigns();
    }
  }, [open]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/campaigns');
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      toast.error('Failed to load campaigns');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.campaign_id) {
      toast.error('Please select a campaign');
      return;
    }

    setLoading(true);
    try {
      // Add entity to campaign using new API
      await api.post(`/campaigns/${formData.campaign_id}/entities`, {
        crm_entity_id: accountId,
        contact_entry_id: formData.contact_entry_id || null,
        notes: ''
      });
      
      toast.success('Added to campaign');
      onSuccess?.();
      onOpenChange(false);
      setFormData({
        campaign_id: '',
        contact_entry_id: '',
      });
    } catch (error) {
      console.error('Failed to add to campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to campaign');
    } finally {
      setLoading(false);
    }
  };

  const selectedCampaign = campaigns.find(c => c.id === formData.campaign_id);
  const filteredContacts = contactEntries.filter(entry => {
    if (!selectedCampaign) return false;
    
    // Filter contacts based on campaign type
    const campaignType = selectedCampaign.campaign_type;
    if (campaignType === 'Email') {
      return entry.contact_type === 'email';
    } else if (campaignType === 'Phone') {
      return entry.contact_type === 'phone';
    } else if (campaignType === 'Direct Mail') {
      return entry.contact_type === 'address';
    }
    return true; // For 'other' type, show all
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="marketing-dialog">
        <DialogHeader>
          <DialogTitle>Add to Marketing Campaign</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Select Campaign *</Label>
            <Select 
              value={formData.campaign_id} 
              onValueChange={(val) => setFormData({ ...formData, campaign_id: val, contact_entry_id: '' })}
            >
              <SelectTrigger data-testid="campaign-select">
                <SelectValue placeholder="Select a campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map(campaign => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.campaign_type?.replace('_', ' ')})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCampaign && (
            <div className="space-y-2">
              <Label>Contact Detail to Use</Label>
              <Select 
                value={formData.contact_entry_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, contact_entry_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger data-testid="contact-detail-select">
                  <SelectValue placeholder="Select contact detail" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Use any available</SelectItem>
                  {filteredContacts.length === 0 ? (
                    <SelectItem value="no-contacts" disabled>
                      No {selectedCampaign.campaign_type === 'Email' ? 'email addresses' : 
                         selectedCampaign.campaign_type === 'Phone' ? 'phone numbers' : 
                         selectedCampaign.campaign_type === 'Direct Mail' ? 'addresses' : 'contacts'} on file
                    </SelectItem>
                  ) : (
                    filteredContacts.map(entry => (
                      <SelectItem key={entry.id} value={entry.id}>
                        {entry.contact_type === 'email' && `${entry.email_address} (Email${entry.email_type ? ' - ' + entry.email_type : ''})`}
                        {entry.contact_type === 'phone' && `${entry.phone_number} (Phone${entry.phone_type ? ' - ' + entry.phone_type : ''})`}
                        {entry.contact_type === 'address' && `${entry.street}, ${entry.city} (Address${entry.address_type ? ' - ' + entry.address_type : ''})`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {filteredContacts.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add contact details on the &quot;Contact Details&quot; tab to use specific {
                    selectedCampaign.campaign_type === 'email' ? 'email addresses' : 
                    selectedCampaign.campaign_type === 'telephone' ? 'phone numbers' : 
                    selectedCampaign.campaign_type === 'direct_mailer' ? 'mailing addresses' : 'contacts'
                  } for this campaign.
                </p>
              )}
            </div>
          )}

          {selectedCampaign && (
            <div className="p-3 bg-muted/30 rounded text-sm">
              <p className="font-medium mb-1">{selectedCampaign.name}</p>
              <p className="text-muted-foreground text-xs">
                Type: {selectedCampaign.campaign_type?.replace('_', ' ')} • 
                Frequency: {selectedCampaign.frequency?.replace('_', ' ')}
              </p>
              {selectedCampaign.notes && (
                <p className="text-muted-foreground text-xs mt-2">{selectedCampaign.notes}</p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !formData.campaign_id} data-testid="mkt-submit-btn">
              {loading ? 'Adding...' : 'Add to Campaign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

